import React, { useState } from 'react';
import { FaChevronDown, FaChevronRight, FaTimesCircle, FaExclamationTriangle, FaInfoCircle, FaRegCircle } from 'react-icons/fa';

// Columns summarised in the collapsed row; everything else shows when expanded.
const SUMMARY_COLUMNS = [
    ['polymer_1', 'Polymer'],
    ['solvent_1', 'Solvent'],
    ['voltage', 'Voltage'],
    ['flow_rate', 'Flow rate'],
    ['tip_collector_distance', 'Distance'],
    ['fiber_diameter', 'Diameter'],
];

const PAGE_SIZE = 50;

const SEVERITY_ICON = {
    ERROR: <FaTimesCircle className="text-danger" />,
    MISSING: <FaRegCircle className="bulk-text-incomplete" />,
    WARNING: <FaExclamationTriangle className="text-warning" />,
    INFO: <FaInfoCircle className="text-secondary" />,
};

const REFERENCES = [['solution', ''], ['core_solution', 'core '], ['middle_solution', 'middle '], ['shell_solution', 'shell ']];

/** "15" + unit column "kV" -> "15 kV"; a run using the Solutions sheet shows its solution ids and channel flow rates. */
const withUnit = (values, key) => {
    if (key === 'polymer_1' && values[key] == null) {
        const refs = REFERENCES.filter(([k]) => values[k]).map(([k, label]) => `${label}${values[k]}`);
        if (refs.length) return `→ ${refs.join(', ')}`;
    }
    if (key === 'flow_rate' && values[key] == null) {
        const rates = ['flow_rate_core', 'flow_rate_middle', 'flow_rate_shell'].filter(k => values[k]).map(k => values[k]);
        if (rates.length) return `${rates.join(' + ')} ${values.flow_rate_unit || 'mL/h'}`;
    }
    const v = values[key];
    if (v == null) return '';
    const unit = values[`${key}_unit`];
    return unit ? `${v} ${unit}` : v;
};

function CellEditor({ row, column, onEdit, disabled }) {
    const [value, setValue] = useState(row.values[column] ?? '');
    const changed = value !== (row.values[column] ?? '');
    return (
        <form className="d-flex gap-2 align-items-center bulk-cell-editor"
            onSubmit={e => { e.preventDefault(); if (changed) onEdit(row.rowNumber, column, value.trim()); }}>
            <label className="small text-muted mb-0" htmlFor={`edit-${row.rowNumber}-${column}`}><code>{column}</code></label>
            <input id={`edit-${row.rowNumber}-${column}`} className="form-control form-control-sm"
                value={value} onChange={e => setValue(e.target.value)} disabled={disabled} />
            <button type="submit" className="btn btn-sm btn-outline-primary" disabled={disabled || !changed}>Apply</button>
        </form>
    );
}

const PROPOSABLE = { UNKNOWN_POLYMER: 'POLYMER', UNKNOWN_SOLVENT: 'SOLVENT', UNKNOWN_ADDITIVE: 'ADDITIVE' };

/** Proposes an unknown polymer/solvent name for the vocabulary; an admin decides later. */
function ProposeForm({ kind, name, onPropose, onCancel, disabled }) {
    const [fullName, setFullName] = useState('');
    const [casNumber, setCasNumber] = useState('');
    const [note, setNote] = useState('');
    const what = kind.toLowerCase();
    const id = `propose-${kind}-${name}`.replace(/\W+/g, '-');
    return (
        <form className="bulk-propose" onSubmit={e => {
            e.preventDefault();
            onPropose({ kind, name, fullName: fullName.trim(), casNumber: casNumber.trim(), note: note.trim() });
        }}>
            <div className="small mb-2">
                Propose <strong>{name}</strong> as a new {what}. Rows using it are saved as <em>Needs update</em> and
                completed automatically once an admin accepts the name. Details help the admin decide.
            </div>
            <div className="row g-2">
                <div className="col-sm-6">
                    <label className="form-label small mb-1" htmlFor={`${id}-full`}>Full name (optional)</label>
                    <input id={`${id}-full`} className="form-control form-control-sm" value={fullName}
                        onChange={e => setFullName(e.target.value)} placeholder={kind === 'POLYMER' ? 'e.g. Polyhydroxybutyrate-co-valerate' : 'e.g. Hexafluoroisopropanol'} />
                </div>
                <div className="col-sm-6">
                    <label className="form-label small mb-1" htmlFor={`${id}-cas`}>CAS number (optional)</label>
                    <input id={`${id}-cas`} className="form-control form-control-sm" value={casNumber}
                        onChange={e => setCasNumber(e.target.value)} placeholder="e.g. 80181-31-3" />
                </div>
                <div className="col-12">
                    <label className="form-label small mb-1" htmlFor={`${id}-note`}>Note for the admin (optional)</label>
                    <input id={`${id}-note`} className="form-control form-control-sm" value={note}
                        onChange={e => setNote(e.target.value)} />
                </div>
            </div>
            <div className="d-flex gap-2 mt-2">
                <button type="submit" className="btn btn-sm btn-primary bulk-primary" disabled={disabled}>Propose “{name}”</button>
                <button type="button" className="btn btn-sm btn-link" onClick={onCancel}>Cancel</button>
            </div>
        </form>
    );
}

function RowDetails({ row, allRows, onEdit, onPropose, onWithdraw, proposals, disabled }) {
    const [proposing, setProposing] = useState(null); // issue index whose propose form is open
    const visibleIssues = row.issues.filter(i => i.severity !== 'INFO');
    const notes = row.issues.filter(i => i.severity === 'INFO');
    const editable = [...new Set(row.issues
        .filter(i => i.column && (i.severity === 'ERROR' || i.severity === 'MISSING'))
        .map(i => i.column))];

    return (
        <div className="bulk-row-details">
            {visibleIssues.length > 0 && (
                <ul className="list-unstyled mb-3">
                    {visibleIssues.map((issue, i) => {
                        const original = issue.column ? row.values[issue.column] : undefined;
                        const sameValueRows = original
                            ? allRows.filter(r => r.values[issue.column] === original).length : 0;
                        return (
                            <li key={i} className="bulk-issue">
                                {SEVERITY_ICON[issue.severity]}
                                <div>
                                    {issue.column && <code className="me-2">{issue.column}</code>}
                                    {issue.message}
                                    {(issue.suggestions?.length > 0 || PROPOSABLE[issue.code]) && (
                                        <div className="d-flex flex-wrap gap-2 mt-1">
                                            {issue.suggestions.map(s => (
                                                <React.Fragment key={s}>
                                                    <button className="btn btn-sm btn-outline-primary" disabled={disabled}
                                                        onClick={() => onEdit(row.rowNumber, issue.column, s)}>
                                                        Use “{s}”
                                                    </button>
                                                    {sameValueRows > 1 && (
                                                        <button className="btn btn-sm btn-outline-secondary" disabled={disabled}
                                                            onClick={() => onEdit(row.rowNumber, issue.column, s, true)}>
                                                            Use “{s}” in all {sameValueRows} rows with “{original}”
                                                        </button>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                            {PROPOSABLE[issue.code] && proposing !== i && (
                                                <button className="btn btn-sm btn-outline-success" disabled={disabled}
                                                    onClick={() => setProposing(i)}>
                                                    Propose “{issue.value}” as a new {PROPOSABLE[issue.code].toLowerCase()}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    {PROPOSABLE[issue.code] && proposing === i && (
                                        <ProposeForm kind={PROPOSABLE[issue.code]} name={issue.value} disabled={disabled}
                                            onPropose={p => { setProposing(null); onPropose(p); }}
                                            onCancel={() => setProposing(null)} />
                                    )}
                                    {issue.code === 'PROPOSED' && proposals.some(p => p.name.toLowerCase() === issue.value?.toLowerCase()) && (
                                        <div className="mt-1">
                                            <button className="btn btn-sm btn-link p-0" disabled={disabled}
                                                onClick={() => onWithdraw(issue.column.startsWith('polymer') ? 'POLYMER' : issue.column.startsWith('additive') ? 'ADDITIVE' : 'SOLVENT', issue.value)}>
                                                Withdraw this proposal
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            {editable.length > 0 && (
                <div className="mb-3">
                    <div className="small fw-semibold mb-2">Fix values here (or correct the file and upload it again):</div>
                    <div className="d-flex flex-column gap-2">
                        {editable.map(column => (
                            <CellEditor key={`${column}-${row.values[column] ?? ''}`} row={row} column={column}
                                onEdit={onEdit} disabled={disabled} />
                        ))}
                    </div>
                </div>
            )}

            {notes.length > 0 && (
                <details className="small text-muted mb-2">
                    <summary>{notes.length} automatic correction{notes.length === 1 ? '' : 's'}</summary>
                    <ul className="mb-0 mt-1">
                        {notes.map((n, i) => <li key={i}>{n.column && <code className="me-1">{n.column}</code>}{n.message}</li>)}
                    </ul>
                </details>
            )}

            <details className="small">
                <summary>All values in this row</summary>
                <dl className="bulk-values mt-2 mb-0">
                    {Object.entries(row.values).map(([k, v]) => (
                        <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>
                    ))}
                </dl>
            </details>
        </div>
    );
}

export default function ImportRowsTable({ rows, allRows, statusMeta, excluded, onToggleExcluded, onEdit, onPropose, onWithdraw, proposals, disabled }) {
    const [expanded, setExpanded] = useState(() => new Set());
    const [page, setPage] = useState(0);

    const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    const current = Math.min(page, pages - 1);
    const visible = rows.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE);

    const toggle = (rowNumber) => setExpanded(prev => {
        const next = new Set(prev);
        if (next.has(rowNumber)) next.delete(rowNumber);
        else next.add(rowNumber);
        return next;
    });

    if (rows.length === 0) {
        return <div className="bulk-card text-muted small">No rows match this filter.</div>;
    }

    return (
        <div className="bulk-table-wrap">
            <div className="table-responsive">
                <table className="table table-sm align-middle mb-0 bulk-table">
                    <thead>
                        <tr>
                            <th scope="col" style={{ width: 32 }}><span className="visually-hidden">Expand</span></th>
                            <th scope="col">Row</th>
                            <th scope="col">Status</th>
                            <th scope="col">DOI</th>
                            {SUMMARY_COLUMNS.map(([, label]) => <th scope="col" key={label}>{label}</th>)}
                            <th scope="col">Issues</th>
                            <th scope="col" className="text-end">Include</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map(row => {
                            const meta = statusMeta[row.status];
                            const isOpen = expanded.has(row.rowNumber);
                            const isExcluded = excluded.has(row.rowNumber);
                            const problems = row.issues.filter(i => i.severity !== 'INFO');
                            const badCells = new Set(row.issues.filter(i => i.severity === 'ERROR' || i.severity === 'MISSING')
                                .map(i => i.column?.replace(/_unit$/, '')));
                            return (
                                <React.Fragment key={row.rowNumber}>
                                    <tr className={`${isExcluded ? 'bulk-excluded' : ''} ${isOpen ? 'bulk-open' : ''}`}>
                                        <td>
                                            <button className="btn btn-link btn-sm p-0" onClick={() => toggle(row.rowNumber)}
                                                aria-expanded={isOpen} aria-label={`${isOpen ? 'Hide' : 'Show'} details of row ${row.rowNumber}`}>
                                                {isOpen ? <FaChevronDown /> : <FaChevronRight />}
                                            </button>
                                        </td>
                                        <td>{row.rowNumber}</td>
                                        <td><span className={`bulk-status ${meta.className}`}>{meta.label}</span></td>
                                        <td className="bulk-doi" title={row.values.doi}>{row.values.doi || '—'}</td>
                                        {SUMMARY_COLUMNS.map(([key]) => (
                                            <td key={key} className={badCells.has(key) ? 'bulk-bad-cell' : ''}>
                                                {withUnit(row.values, key) || <span className="text-muted">—</span>}
                                            </td>
                                        ))}
                                        <td>
                                            {problems.length > 0
                                                ? <button className="btn btn-link btn-sm p-0" onClick={() => toggle(row.rowNumber)}>{problems.length} to review</button>
                                                : <span className="text-muted small">none</span>}
                                        </td>
                                        <td className="text-end">
                                            <input type="checkbox" className="form-check-input" checked={!isExcluded}
                                                onChange={() => onToggleExcluded(row.rowNumber)}
                                                aria-label={`Include row ${row.rowNumber}`} />
                                        </td>
                                    </tr>
                                    {isOpen && (
                                        <tr className="bulk-details-row">
                                            <td colSpan={SUMMARY_COLUMNS.length + 6}>
                                                <RowDetails row={row} allRows={allRows} onEdit={onEdit} onPropose={onPropose}
                                                    onWithdraw={onWithdraw} proposals={proposals} disabled={disabled} />
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {pages > 1 && (
                <div className="d-flex justify-content-between align-items-center p-2 small">
                    <span className="text-muted">Rows {current * PAGE_SIZE + 1}–{Math.min(rows.length, (current + 1) * PAGE_SIZE)} of {rows.length}</span>
                    <div className="btn-group btn-group-sm">
                        <button className="btn btn-outline-secondary" disabled={current === 0} onClick={() => setPage(current - 1)}>Previous</button>
                        <button className="btn btn-outline-secondary" disabled={current >= pages - 1} onClick={() => setPage(current + 1)}>Next</button>
                    </div>
                </div>
            )}
        </div>
    );
}
